import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

interface CharacterCountProps {
  /** Current character count in UTF-16 code units */
  count: number;
  /** Maximum allowed characters. 4096 for messages, 1024 for captions */
  limit: number;
}

/**
 * Character counter component with color-coded status.
 * 
 * Displays the current character count against the limit with visual
 * indicators based on usage percentage:
 * - Green (< 80%): Normal usage
 * - Yellow (80-95%): Approaching limit
 * - Red (> 95%): Near limit
 * - Red bold (> 100%): Exceeded limit
 */
export function CharacterCount({ count, limit }: CharacterCountProps) {
  const { t } = useTranslation('preview');
  const percentage = (count / limit) * 100;

  const statusClass = cn({
    'text-green-600 dark:text-green-400': percentage < 80,
    'text-yellow-600 dark:text-yellow-400': percentage >= 80 && percentage <= 95,
    'text-red-600 dark:text-red-400': percentage > 95 && percentage <= 100,
    'text-red-600 dark:text-red-400 font-bold': percentage > 100,
  });

  return (
    <div className={cn('flex items-center gap-1 text-xs', statusClass)}>
      <span className="cc-count tabular-nums">{count.toLocaleString()}</span>
      <span className="cc-separator opacity-60">/</span>
      <span className="cc-limit tabular-nums">{limit.toLocaleString()}</span>
      <span className="cc-label ml-1 opacity-60">{t('utf16')}</span>
    </div>
  );
}
